import { Fragment, useEffect, useMemo, useState } from 'react'

import { Card, CardContent } from '@/components/ui/card.tsx'
import { Separator } from '@/components/ui/separator.tsx'

import { EstimateIcon } from '@/components/EstimateIcon.tsx'
import { IterationSelector } from '@/components/IterationSelector.tsx'
import { MemberStringList } from '@/components/MemberStringList.tsx'
import { StoryTypeIcon } from '@/components/StoryTypeIcon.tsx'
import { StoryLabel } from '@/components/form/StoryLabel.tsx'
import { useUserContext } from '@/contexts/UserContext.tsx'
import { useQueryAnalyticsIterations } from '@/requests/queries/useQueryAnalytics.tsx'
import { toReadableDate } from '@/utils/date.ts'
import { isPointAssignable } from '@/utils/story.ts'

export const ProjectAnalyticsAcceptedStoriesPage = () => {
  const { apiKey } = useUserContext()
  const { iterations } = useQueryAnalyticsIterations(apiKey)
  const [selectedIteration, setSelectedIteration] = useState<IterationSummary | undefined>(
    iterations?.[iterations.length - 1],
  )

  useEffect(() => {
    setSelectedIteration(iterations?.[iterations.length - 1])
  }, [iterations])

  const groupedStoriesByDate = useMemo(() => {
    if (!selectedIteration) return []

    const groupedStories = selectedIteration.stories.reduce(
      (acc, story) => {
        if (!story.acceptedAt) return acc
        const date = getStartOfDay(story.acceptedAt)
        if (!acc[date]) {
          acc[date] = {
            date,
            stories: [],
          } as StoriesByDay
        }
        acc[date].stories.push(story)
        return acc
      },
      {} as Record<number, StoriesByDay>,
    )

    return Object.values(groupedStories)
  }, [selectedIteration])

  if (!selectedIteration) return

  return (
    <>
      <p className="font-EN-KR-sub-heading-bold text-contents-primary !font-semibold">Stories Accepted Report</p>
      <Separator className="mt-2.5 mb-4 bg-border-default-secondary" />
      <div className="w-full flex gap-1 justify-between mb-2 h-8 items-center">
        <p className="font-EN-KR-sub-heading-bold text-contents-tertiary">Iteration</p>
        <IterationSelector iterations={iterations} setSelectedIteration={setSelectedIteration} />
      </div>
      <div className="flex flex-col gap-2">
        {groupedStoriesByDate.map((group, index) => {
          return <StoriesByDayCard key={group.date} date={group.date} stories={group.stories} isFirst={index === 0} />
        })}
      </div>
    </>
  )
}

type StoriesByDayCardProps = {
  date: number
  stories: StoryWithAnalyticsDetails[]
  isFirst?: boolean
}

const StoriesByDayCard = ({ date, stories, isFirst }: StoriesByDayCardProps) => {
  return (
    <Card className="w-full flex flex-col gap-2">
      <CardContent className="w-full bg-surface-backgroundElevated rounded-md p-2">
        <div className="flex justify-between p-2">
          <p className="font-EN-KR-body1-bold text-contents-primary">{toReadableDate(date)}</p>
          {isFirst && (
            <p className="font-EN-KR-body2-regular text-contents-tertiary">
              All timestamps are shown in the project timezone (Asia/Tokyo)
            </p>
          )}
        </div>
        <div className="rounded-sm overflow-hidden">
          {stories.map((story) => (
            <StoryListElement key={story.id} story={story} />
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

const StoryListElement = ({ story }: { story: StoryWithAnalyticsDetails }) => {
  return (
    <Card className="border-0 rounded-none shadow-none cursor-default">
      <CardContent className="flex items-start gap-2 p-2 font-EN-KR-body1-regular relative bg-surface-neutralLight hover:after:bg-stateLayer-onLight-hovered hover:after:opacity-8 after:absolute after:inset-0 after:pointer-events-none shadow-[inset_4px_0_0_0_theme(colors.surface.storyStatusBar.toDo)]">
        <div className="flex flex-col items-center h-5">
          <div className="h-5 flex items-center justify-center p-0 rounded-xs">
            <StoryTypeIcon className="w-4 h-4" storyType={story.storyType} />
          </div>
        </div>

        <div className="h-5 w-[14px]">
          {isPointAssignable(story.storyType) && (
            <EstimateIcon className="h-5 w-[14px] rounded-sm" estimate={story.estimate} />
          )}
        </div>

        <div className="flex flex-col items-start flex-1 break-all">
          <div className="text-[14px] leading-5">
            {
              <span className="text-contents-primary">
                {story.title}
                <MemberStringList targetMembers={story.owners} />
              </span>
            }
          </div>

          <div className="flex flex-wrap items-center">
            {story.labels.map((label, index) => (
              <Fragment key={label.id}>
                <div className="bg-transparent text-labelType-label px-0 cursor-pointer">
                  <StoryLabel item={label.name} variant="text" className="font-EN-KR-tag-bold" />
                </div>
                {index < story.labels.length - 1 && (
                  <span className="text-contents-primary font-EN-KR-tag-bold">,&nbsp;</span>
                )}
              </Fragment>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

type StoriesByDay = {
  date: number
  stories: StoryWithAnalyticsDetails[]
}

const getStartOfDay = (timestamp: number) => {
  const date = new Date(timestamp)
  date.setHours(0, 0, 0, 0)
  return date.getTime()
}
